import { Vector2 } from "../../../common/vector2.js";
import { Game } from "../../../game.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { ADVENTURE_REWARD_ICONS, type AdventureBoxReward } from "../../../adventure/adventure_box_rewards.js";
import { rewardAmountText } from "../../../common/reward_format.js";
import { StatisticsStorage } from "../../../storage/statistics_storage.js";
import type { GameStatistics } from "../../../storage/statistics_storage.js";
import { TournamentService, type TournamentBox } from "../../../tournament/tournament_service.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { TransformStraightMove } from "../../../transforms/transform_straight_move.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { RewardFrame } from "../../common_ui_elements/reward_frame.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UICanvasRect } from "../../common_ui_elements/ui_canvas_rect.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement, type UITextOptions } from "../../common_ui_elements/ui_text_element.js";
import { TournamentTitle } from "../../dialog/tournament_title.js";
import { RewardFlySource, RewardFlyToHud } from "../../reward_fly/reward_fly_to_hud.js";
import { AnchoredTooltip } from "../../tooltip/anchored_tooltip.js";
import { RewardTooltip } from "../../tooltip/reward_tooltip.js";
import { AbstractOverlay } from "../abstract_overlay.js";
import { OverlayId } from "../overlay_ids.js";
import { OverlayManager } from "../overlay_manager.js";

const CLOSED_BOX_IMAGES: Record<TournamentBox, ImgID> = {
  green: ImgID.UI_TournamentBoxGreen,
  blue: ImgID.UI_TournamentBoxBlue,
  red: ImgID.UI_TournamentBoxRed,
  purple: ImgID.UI_TournamentBoxPurple,
};

const OPEN_BOX_IMAGES: Record<TournamentBox, ImgID> = {
  green: ImgID.UI_TournamentBoxGreenOpen,
  blue: ImgID.UI_TournamentBoxBlueOpen,
  red: ImgID.UI_TournamentBoxRedOpen,
  purple: ImgID.UI_TournamentBoxPurpleOpen,
};

export interface TournamentWinOverlayParams
{
  place?: number;
  rewards?: AdventureBoxReward[];
  /** Collects the place reward on the server; the fly animation plays only when it resolves true. */
  on_claim?: () => Promise<boolean>;
  on_closed?: () => void;
  /** The progress bar's white grand prize chest instead of a place box: no place line. */
  grand_prize?: boolean;
}

export class TournamentWinOverlay extends AbstractOverlay
{
  private static readonly FRAME_W = 1080;
  private static readonly FRAME_H = 1920;
  private static readonly BANNER = { x: 126, y: 209, w: 829, h: 277 };
  private static readonly BANNER_RISE = 113;
  private static readonly TITLE_CENTER_Y = 323.5;
  private static readonly FINISHED = { center_y: 491.5, w: 900, h: 80, font: 50 };
  private static readonly PLACE = { center_y: 588, w: 940, h: 110, font: 80 };
  private static readonly HINT = { center_y: 1731, w: 940, h: 80, font: 50 };
  private static readonly TEXT_OUTLINE_WIDTH = 10;

  private static readonly CHEST = { x: 371, y: 406, w: 339, h: 324 };
  private static readonly CLOSED_SCALE = 374 / 339;
  private static readonly CLOSED_CENTER = { x: 547, y: 992 };
  private static readonly CARD = { y: 860, w: 186, h: 230, pitch: 209, icon: 159, amount_font: 40, amount_offset_y: 12 };
  private static readonly CARDS_MAX_ROW_W = 960;
  private static readonly BUTTON = { y: 1411, w: 598, h: 274, font: 80 };
  private static readonly SQUASH = { to: 0.825, ms: 200 };
  private static readonly STRETCH = { to: 1.07, ms: 100 };
  private static readonly GROW = { to: 1.185, ms: 100 };
  private static readonly DROP = { to: 0.93, ms: 167 };
  private static readonly SETTLE = { to: 1, ms: 67 };
  private static readonly APPEAR_MS = 200;
  private static readonly RISE_MS = 320;
  private static readonly FADE_MS = 160;
  private static readonly CARD_POP_MS = 150;
  private static readonly CARD_SETTLE_MS = 100;
  private static readonly CARD_STAGGER_MS = 90;
  private static readonly COIN_FLY_SPEED = 1.5;
  private static readonly OPEN_SPEED = 1.5;
  private readonly design_x: number;
  private readonly design_y: number;
  private readonly overlay_params: TournamentWinOverlayParams;
  private readonly place: number;
  private readonly rewards: AdventureBoxReward[];
  private readonly stats_before: GameStatistics;
  private backdrop!: UICanvasRect;
  private banner!: UIImage;
  private title_parts: UIElement[] = [];
  private finished_text!: UITextElement;
  private place_text!: UITextElement;
  private hint!: UITextElement;
  private chest!: UIImage;
  private frames: RewardFrame[] = [];
  private claim_button!: UINineSliceElement;
  private opening = false;
  private claimed = false;
  private close_notified = false;
  constructor( ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, params: TournamentWinOverlayParams | undefined, overlay_id: OverlayId )
  {
    super( ctx, x, y, width, height, overlay_id, params, false );
    this.overlay_params = params ?? {};
    this.place = this.overlay_params.place ?? TournamentService.getFinishedPlace();
    this.rewards = this.overlay_params.rewards ?? TournamentService.getPlaceRewards();
    this.stats_before = StatisticsStorage.load();
    this.design_x = ( this.rect.getW() - LayoutScaler.widthFromDesign( TournamentWinOverlay.FRAME_W ) ) / 2;
    this.design_y = ( this.rect.getH() - LayoutScaler.heightFromDesign( TournamentWinOverlay.FRAME_H ) ) / 2;
    this.createBackdrop();
    this.createBanner();
    this.createTexts();
    this.createChest();
    this.createRewards();
    this.createClaimButton();
    this.playAppear();
  }
  public name(): string { return "TournamentWinOverlay"; }

  private static openDuration( ms: number ): number
  {
    return Math.round( ms / TournamentWinOverlay.OPEN_SPEED );
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }
  public override getViewportBackground(): string | null
  {
    return "transparent";
  }
  public override onOverlayWillClose(): void
  {
    AnchoredTooltip.hide();
    if ( this.close_notified ) return;
    this.close_notified = true;
    this.overlay_params.on_closed?.();
  }

  public onTouchStart( p: Vector2, now: number ): boolean
  {
    super.onTouchStart( p, now );
    this.startOpening();
    return true;
  }
  private x( design_x: number ): number
  {
    return this.design_x + LayoutScaler.xFromDesign( design_x );
  }

  private y( design_y: number ): number
  {
    return this.design_y + LayoutScaler.yFromDesign( design_y );
  }

  private centeredX( design_w: number ): number
  {
    return this.x( ( TournamentWinOverlay.FRAME_W - design_w ) / 2 );
  }

  private createBackdrop(): void
  {
    this.backdrop = new UICanvasRect(
      this.ctx,
      -Game.safeAreaLeft(),
      -Game.safeAreaTop(),
      this.rect.getW() + Game.safeAreaLeft() + Game.safeAreaRight(),
      this.rect.getH() + Game.safeAreaTop() + Game.safeAreaBottom(),
      "rgba(0, 0, 0, 0.8)",
    );
    this.backdrop.setOpacityImmediate( 0 );
    this.backdrop.setOnClick( () => {} );
    this.addChild( this.backdrop );
  }
  private createBanner(): void
  {
    const banner = TournamentWinOverlay.BANNER;
    this.banner = new UIImage(
      this.ctx,
      this.x( banner.x ),
      this.y( banner.y ),
      LayoutScaler.widthFromDesign( banner.w ),
      LayoutScaler.heightFromDesign( banner.h ),
      ImgID.UI_TournamentTopSubstrate,
    );
    this.banner.setOpacityImmediate( 0 );
    this.addChild( this.banner );
    const title_x = this.centeredX( TournamentTitle.TITLE_W );
    const title_y = this.y( TournamentWinOverlay.TITLE_CENTER_Y - TournamentTitle.TITLE_H / 2 );
    for ( const layer of TournamentTitle.createTitleLayers( this.ctx, title_x, title_y ) )
    {
      layer.setOpacityImmediate( 0 );
      this.addChild( layer );
      this.title_parts.push( layer );
    }
  }
  private createTexts(): void
  {
    const finished = TournamentWinOverlay.FINISHED;
    const grand_prize = this.overlay_params.grand_prize === true;
    this.finished_text = this.addText( grand_prize ? "GRAND_PRIZE" : "TOURNAMENT.FINISHED", finished, {} );

    const place = TournamentWinOverlay.PLACE;
    this.place_text = grand_prize
      ? this.addText( "TOURNAMENT.GRAND_PRIZE_WON", place, {} )
      : this.addText( "TOURNAMENT.YOUR_PLACE", place, {
        text_variable_params: [ String( this.place ) ],
        color_map: { yellow: ColorScheme.tournament_place_number },
      } );
    const hint = TournamentWinOverlay.HINT;
    this.hint = this.addText( "TOURNAMENT.TAP_TO_CONTINUE", hint, {
      outline_width: 0,
      ...TextStyleScheme.buttonShadow(),
    } );
  }
  private addText(
    localization_key: string,
    line: { center_y: number; w: number; h: number; font: number },
    extra: Partial<UITextOptions>
  ): UITextElement
  {
    const text = new UITextElement(
      this.ctx,
      this.centeredX( line.w ),
      this.y( line.center_y - line.h / 2 ),
      LayoutScaler.widthFromDesign( line.w ),
      LayoutScaler.heightFromDesign( line.h ),
      {
        localization_key,
        font_size: LayoutScaler.textSizeFromDesign( line.font ),
        min_font_size: LayoutScaler.textSizeFromDesign( Math.round( line.font * 0.6 ) ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        fit_font_size: true,
        outline_width: LayoutScaler.textBorderSizeFromDesign( TournamentWinOverlay.TEXT_OUTLINE_WIDTH ),
        outline_color: ColorScheme.tournament_text_outline,
        ...extra,
      }
    );
    text.setOpacityImmediate( 0 );
    this.addChild( text );
    return text;
  }
  private chestBox(): TournamentBox
  {
    return TournamentService.getPlaceBox( this.place ) ?? "green";
  }

  private createChest(): void
  {
    const chest = TournamentWinOverlay.CHEST;
    const w = LayoutScaler.widthFromDesign( chest.w );
    const h = LayoutScaler.heightFromDesign( chest.h );
    this.chest = new UIImage(
      this.ctx,
      this.x( chest.x ),
      this.y( chest.y ),
      w,
      h,
      this.overlay_params.grand_prize ? ImgID.UI_TournamentBoxWhite : CLOSED_BOX_IMAGES[this.chestBox()],
    );
    this.chest.setOpacityImmediate( 0 );
    this.chest.setScaleImmediate( new Vector2( TournamentWinOverlay.CLOSED_SCALE, TournamentWinOverlay.CLOSED_SCALE ) );
    this.chest.setPixelPos( this.closedChestPos(), true );
    this.addChild( this.chest );
  }
  private closedChestPos(): Vector2
  {
    const chest = TournamentWinOverlay.CHEST;
    const closed = TournamentWinOverlay.CLOSED_CENTER;
    return new Vector2(
      this.x( closed.x ) - LayoutScaler.widthFromDesign( chest.w ) / 2,
      this.y( closed.y ) - LayoutScaler.heightFromDesign( chest.h ) / 2,
    );
  }
  private openedChestPos(): Vector2
  {
    const chest = TournamentWinOverlay.CHEST;
    return new Vector2( this.x( chest.x ), this.y( chest.y ) );
  }
  private createRewards(): void
  {
    if ( this.rewards.length === 0 ) return;
    const card = TournamentWinOverlay.CARD;
    const natural_row_w = ( this.rewards.length - 1 ) * card.pitch + card.w;

    const scale = Math.min( 1, TournamentWinOverlay.CARDS_MAX_ROW_W / natural_row_w );
    const card_w = card.w * scale;
    const card_h = card.h * scale;
    const pitch = card.pitch * scale;
    const icon_size = LayoutScaler.widthFromDesign( card.icon * scale );
    const row_w = ( this.rewards.length - 1 ) * pitch + card_w;
    const row_y = card.y + ( card.h - card_h ) / 2;
    this.rewards.forEach( ( reward, index ) =>
    {
      const frame = new RewardFrame( this.ctx, this.x( ( TournamentWinOverlay.FRAME_W - row_w ) / 2 + index * pitch ), this.y( row_y ), {
        icon_id: reward.type === "coin" ? ImgID.UI_ShopBlockImg : ADVENTURE_REWARD_ICONS[reward.type],
        amount_text: reward.type === "coin" ? String( reward.amount ) : rewardAmountText( reward.type, reward.amount ),
        design_w: card_w,
        design_h: card_h,
        icon_width: reward.type === "coin" ? LayoutScaler.widthFromDesign( 174 * scale ) : icon_size,
        icon_height: reward.type === "coin" ? LayoutScaler.heightFromDesign( 140 * scale ) : icon_size,
        amount_font_size: card.amount_font * scale,
        amount_offset_y: card.amount_offset_y * scale,
      } );
      frame.setOpacityImmediate( 0 );
      frame.setScaleImmediate( new Vector2( 0, 0 ) );
      RewardTooltip.attachLegacy( frame, reward.type );
      this.addChild( frame );
      this.frames.push( frame );
    } );
  }
  private createClaimButton(): void
  {
    const button = TournamentWinOverlay.BUTTON;
    const w = LayoutScaler.widthFromDesign( button.w );
    const h = LayoutScaler.heightFromDesign( button.h );
    this.claim_button = new UINineSliceElement(
      this.ctx,
      this.centeredX( button.w ),
      this.y( button.y ),
      w,
      h,
      ImgID.UI_GreenFrameBtn,
      "frame_btn",
      undefined,
      undefined,
      true,
    );
    this.claim_button.addChild( new UITextElement( this.ctx, 0, LayoutScaler.yFromDesign( 4 ), w, h, {
      localization_key: "CLAIM",
      font_size: LayoutScaler.textSizeFromDesign( button.font ),
      min_font_size: LayoutScaler.textSizeFromDesign( 44 ),
      font_weight: 900,
      text_style: TextStyleScheme.geologica,
      text_color: ColorScheme.white,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      auto_wrap: false,
      scale_to_width: true,
      ...TextStyleScheme.buttonShadow(),
      outline_color: ColorScheme.more_lives_buy_outline,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
      outline_offset_y: LayoutScaler.yFromDesign( 2 ),
    } ), true );
    this.claim_button.setOpacityImmediate( 0 );
    this.claim_button.setClickable( false );
    this.claim_button.setOnClick( () => this.claim() );
    this.addChild( this.claim_button );
  }
  private playAppear(): void
  {
    const now = performance.now();
    const ms = TournamentWinOverlay.APPEAR_MS;
    this.backdrop.fadeIn( ms, now, Easing.EaseOutQuad );
    for ( const element of [ this.banner, ...this.title_parts, this.finished_text, this.place_text, this.hint ] )
      element.fadeIn( ms, now, Easing.EaseOutQuad );
    this.chest.fadeIn( ms, now, Easing.EaseOutQuad );
    const closed = TournamentWinOverlay.CLOSED_SCALE;
    this.chest.addTransform( new TransformScale(
      this.chest, true, new Vector2( closed * 0.6, closed * 0.6 ), new Vector2( closed, closed ),
      now, ms + 60, { easing: Easing.EaseOutQuad },
    ) );
  }

  private startOpening(): void
  {
    if ( this.opening ) return;
    this.opening = true;
    const now = performance.now();
    const rise_ms = TournamentWinOverlay.openDuration( TournamentWinOverlay.RISE_MS );
    for ( const element of [ this.finished_text, this.place_text, this.hint ] )
      element.fadeOut( TournamentWinOverlay.openDuration( TournamentWinOverlay.FADE_MS ), now, Easing.EaseInQuad );
    const rise = LayoutScaler.yFromDesign( TournamentWinOverlay.BANNER_RISE );
    for ( const element of [ this.banner, ...this.title_parts ] )
    {
      const pos = element.pixelPosImportant();
      element.addTransform( new TransformStraightMove(
        element, true, null, new Vector2( pos.x, pos.y - rise ), now, rise_ms, { easing: Easing.EaseInOutQuad },
      ) );
    }
    this.chest.addTransform( new TransformScale(
      this.chest, true, null, new Vector2( 1, 1 ), now, rise_ms, { easing: Easing.EaseInOutQuad },
    ) );
    const move = new TransformStraightMove(
      this.chest, true, null, this.openedChestPos(), now, rise_ms, { easing: Easing.EaseInOutQuad },
    );
    move.setOnFinished( () => this.playOpen() );
    this.chest.addTransform( move );
  }
  private playOpen(): void
  {
    const ground = this.openedChestPos().y + this.chest.getRect().getH();
    const step = ( from: number, to: number, ms: number, easing: Easing, done?: () => void ): void =>
      this.scaleFromBottom( ground, from, to, TournamentWinOverlay.openDuration( ms ), easing, done );

    step( 1, TournamentWinOverlay.SQUASH.to, TournamentWinOverlay.SQUASH.ms, Easing.EaseInQuad, () =>
      step( TournamentWinOverlay.SQUASH.to, TournamentWinOverlay.STRETCH.to, TournamentWinOverlay.STRETCH.ms, Easing.EaseOutQuad, () =>
      {
        this.chest.setImageId( this.overlay_params.grand_prize ? ImgID.UI_TournamentBoxWhiteOpen : OPEN_BOX_IMAGES[this.chestBox()] );
        step( TournamentWinOverlay.STRETCH.to, TournamentWinOverlay.GROW.to, TournamentWinOverlay.GROW.ms, Easing.EaseOutQuad, () =>
          step( TournamentWinOverlay.GROW.to, TournamentWinOverlay.DROP.to, TournamentWinOverlay.DROP.ms, Easing.EaseInOutQuad, () =>
            step( TournamentWinOverlay.DROP.to, TournamentWinOverlay.SETTLE.to, TournamentWinOverlay.SETTLE.ms, Easing.EaseOutQuad,
              () => this.revealRewards() ) ) );
      } ) );
  }

  private scaleFromBottom( ground_y: number, from: number, to: number, ms: number, easing: Easing, done?: () => void ): void
  {
    const start = performance.now();
    const opened = this.openedChestPos();
    const distance = opened.y + this.chest.getRect().getH() / 2 - ground_y;
    const scale = new TransformScale( this.chest, true, new Vector2( 1, from ), new Vector2( 1, to ), start, ms, { easing } );
    if ( done ) scale.setOnFinished( done );
    this.chest.addTransform( scale );
    this.chest.addTransform( new TransformStraightMove(
      this.chest, false,
      new Vector2( 0, distance * ( from - 1 ) ),
      new Vector2( 0, distance * ( to - 1 ) ),
      start, ms, { easing },
    ) );
  }
  private revealRewards(): void
  {
    const now = performance.now();
    const pop = TournamentWinOverlay.openDuration( TournamentWinOverlay.CARD_POP_MS );
    const settle = TournamentWinOverlay.openDuration( TournamentWinOverlay.CARD_SETTLE_MS );
    const stagger = TournamentWinOverlay.openDuration( TournamentWinOverlay.CARD_STAGGER_MS );
    this.frames.forEach( ( frame, index ) =>
    {
      const start = now + index * stagger;
      frame.fadeIn( pop, start, Easing.EaseOutQuad );
      frame.addTransform( new TransformScale( frame, true, new Vector2( 0, 0 ), new Vector2( 1.15, 1.15 ), start, pop, { easing: Easing.EaseOutQuad } ) );
      frame.addTransform( new TransformScale( frame, true, new Vector2( 1.15, 1.15 ), new Vector2( 1, 1 ), start + pop, settle, { easing: Easing.EaseInQuad } ) );
    } );
    const button_at = now + Math.max( 0, this.frames.length - 1 ) * stagger + pop;
    this.claim_button.fadeIn( TournamentWinOverlay.openDuration( 180 ), button_at, Easing.EaseOutQuad );
    RewardFlyToHud.schedule( this.ctx, this, now, button_at, () => this.claim_button.setClickable( true ) );
  }
  private claim(): void
  {
    if ( this.claimed ) return;
    this.claimed = true;
    this.claim_button.setClickable( false );
    const request = this.overlay_params.on_claim ? this.overlay_params.on_claim() : Promise.resolve( true );
    void request.then( ok =>
    {
      if ( ok ) this.playClaim();
      else if ( OverlayManager.isOverlayOpen( this.getId() ) ) OverlayManager.closeOverlay();
    } );
  }

  private playClaim(): void
  {
    const sources = this.frames.map( ( frame, index ): RewardFlySource =>
    {
      const reward = this.rewards[index];
      const is_heart = reward.type === "heart" || reward.type === "infinite_hearts";
      const pos = frame.icon.getAbsolutePosition();
      const rect = frame.icon.getRect();
      return {
        target: reward.type === "coin" ? "coin" : is_heart ? "heart" : "avatar",
        img_id: ADVENTURE_REWARD_ICONS[reward.type],
        center: new Vector2( pos.x + rect.getW() / 2, pos.y + rect.getH() / 2 ),
        size: rect.getW(),
        amount: reward.amount,
        copies: reward.type === "heart" ? Math.max( 1, Math.min( 3, reward.amount ) ) : 1,
      };
    } );
    const now = performance.now();
    const fade = TournamentWinOverlay.FADE_MS;
    for ( const element of [ ...this.frames, this.claim_button, this.banner, ...this.title_parts, this.chest, this.backdrop ] )
      element.fadeOut( fade, now, Easing.EaseInQuad );
    const finish = RewardFlyToHud.play( this.ctx, this, sources, this.stats_before, TournamentWinOverlay.COIN_FLY_SPEED );
    RewardFlyToHud.schedule( this.ctx, this, now, Math.max( finish, now + fade ), () =>
    {
      if ( OverlayManager.isOverlayOpen( this.getId() ) )
        OverlayManager.closeOverlay();
    } );
  }
}
